/**
 * Custom hook for managing transformer attachment
 * Extracted from Canvas.jsx to reduce file size
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import {
  attachTransformerToNode,
  validateNode,
  clearTransformers,
  applyImageNodeProperties,
  findElementCanvas,
} from "./transformerUtils";

/** Clone / duplicate placement: no resize or rotate handles (matches Canvas.jsx enabledAnchors=[]). */
const applyDuplicatePlacementTransformerAttrs = (targetTransformer) => {
  if (!targetTransformer?.setAttrs) return;
  try {
    targetTransformer.setAttrs({
      enabledAnchors: [],
      rotateEnabled: false,
    });
    targetTransformer.getLayer()?.batchDraw();
  } catch (_) {
    // ignore
  }
};

export const useTransformerManager = ({
  selectedObject,
  selectedObjects,
  isTransformingRef,
  isDuplicatePlacementActive = false,
  stageRef,
  offCanvasStageRef,
  topLayerStageRef,
  transformerRef,
  offCanvasTransformerRef,
  topLayerTransformerRef,
  rectangles,
  circles,
  images,
  texts,
  arrows,
  lines,
  doubleSidedArrows,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasImages,
  offCanvasTexts,
  offCanvasArrows,
  offCanvasLines,
  offCanvasDoubleSidedArrows,
  editTextBoxContent,
  showEditNote,
  showEditTask,
}) => {
  // PERFORMANCE FIX: Track last attached node to avoid unnecessary re-attachments
  const lastAttachedNodeRef = useRef(null);

  // CRITICAL FIX: Track if we're in text edit mode to prevent transformer from being cleared
  const isTextEditModeRef = useRef(false);

  // Update text edit mode ref
  useEffect(() => {
    const isNowInEditMode = editTextBoxContent || showEditNote || showEditTask;
    isTextEditModeRef.current = isNowInEditMode;

    if (selectedObject?.type !== "Text") return;

    const anchors = isNowInEditMode ? [] : ["middle-left", "middle-right"];
    [transformerRef, offCanvasTransformerRef, topLayerTransformerRef].forEach(
      (ref) => {
        if (ref?.current && ref.current.nodes().length > 0) {
          ref.current.setAttrs({ enabledAnchors: anchors });
          const layer = ref.current.getLayer();
          if (layer) layer.batchDraw();
        }
      }
    );
  }, [
    editTextBoxContent,
    showEditNote,
    showEditTask,
    selectedObject?.type,
    selectedObject?.id,
    transformerRef,
    offCanvasTransformerRef,
    topLayerTransformerRef,
  ]);

  // NOTE: Text drag-specific anchor hiding is now handled directly in TextElement.jsx


  // REMOVED: This effect was causing transformer anchor issues
  // The main transformer effect below handles all attachment logic correctly
  // This effect was interfering by reattaching the transformer with default settings

  // useLayoutEffect: run before paint so imperative transformer.nodes() does not flash default anchors
  // for one frame (especially clone/duplicate placement where enabledAnchors must stay []).
  useLayoutEffect(() => {
    // CRITICAL FIX: Don't show transformer when multiple objects are selected
    // Transformer should only show for single object selection
    if (selectedObjects && selectedObjects.length > 1) {
      clearTransformers(
        transformerRef,
        offCanvasTransformerRef,
        topLayerTransformerRef
      );
      lastAttachedNodeRef.current = null;
      return;
    }

    if (!selectedObject?.id) {
      lastAttachedNodeRef.current = null;
      return;
    }

    // For non-top-layer images:
    // - main canvas keeps local ImageElement transformer (existing behavior)
    // - off-canvas uses centralized offCanvasTransformerRef so anchors stay above image draws
    if (selectedObject.type === "Image" && !selectedObject.isOnTopLayer) {
      const canvasInfo = findElementCanvas({
        selectedObject,
        rectangles,
        circles,
        images,
        texts,
        arrows,
        lines,
        doubleSidedArrows,
        offCanvasRectangles,
        offCanvasCircles,
        offCanvasImages,
        offCanvasTexts,
        offCanvasArrows,
        offCanvasLines,
        offCanvasDoubleSidedArrows,
      });
      const isInOffCanvas = canvasInfo?.isInOffCanvas === true;
      if (!isInOffCanvas) {
        clearTransformers(
          transformerRef,
          offCanvasTransformerRef,
          topLayerTransformerRef
        );
        lastAttachedNodeRef.current = null;
        return;
      }
    }

    // CRITICAL FIX: Create a cache key that includes geometry/source for images in addition
    // to maintainAspectRatio and canvas location.
    // For replace-image flow, id/canvas/aspect may remain the same while width/height/src change.
    // Including image shape/source fields forces a re-attach so transformer bounds refresh.
    const imageGeometryKey =
      selectedObject.type === "Image"
        ? `${selectedObject.width ?? "w"}-${selectedObject.height ?? "h"}-${selectedObject.scaleX ?? 1}-${selectedObject.scaleY ?? 1}-${selectedObject.src ?? "src"}-${selectedObject._replacedAt ?? "noreplace"}`
        : "";
    // Text: include layout fields in the cache key. Width/height/x/y can change after selection
    // (coalesced layout, measured HTML height) while id stays the same; skipping re-attach left
    // the Transformer stuck on stale bounds until an unrelated state update (e.g. TextBoxWithButtons).
    const textGeometryKey =
      selectedObject.type === "Text"
        ? `${selectedObject.width ?? "w"}-${selectedObject.height ?? "h"}-${selectedObject.x ?? "x"}-${selectedObject.y ?? "y"}-${selectedObject.rotation ?? 0}`
        : "";
    // Including _canvas ensures the transformer re-attaches when an element is dragged between
    // the main canvas and the off-canvas (same id/type, different stage/transformer instance).
    const cacheKey = `${selectedObject.id}-${selectedObject.maintainAspectRatio || false}-${selectedObject._canvas ?? "unknown"}-${imageGeometryKey}${textGeometryKey}-dup-${isDuplicatePlacementActive ? "1" : "0"}`;


    // PERFORMANCE FIX: Skip if we're already attached to this node with same settings
    if (lastAttachedNodeRef.current === cacheKey) {
      // CRITICAL FIX: Even if already attached, check if element became part of multi-selection
      // If so, clear the transformer
      if (selectedObjects && selectedObjects.length > 1) {
        const isInMultiSelect = selectedObjects.some(
          (obj) => obj.id === selectedObject.id && obj.type === selectedObject.type
        );
        if (isInMultiSelect) {
          clearTransformers(
            transformerRef,
            offCanvasTransformerRef,
            topLayerTransformerRef
          );
          lastAttachedNodeRef.current = null;
          return;
        }
      }
      return;
    }

    // Skip transformer attachment if we're currently transforming to prevent fluctuation
    if (isTransformingRef.current.isTransforming) return;

    // CRITICAL FIX: Don't attach transformer for top-layer images when dragging to off-canvas
    if (
      selectedObject?.type === "Image" &&
      selectedObject?.isOnTopLayer &&
      selectedObject?._isDraggingToOffCanvas === true
    ) {
      // Clear transformer if it's attached
      if (topLayerTransformerRef.current) {
        topLayerTransformerRef.current.nodes([]);
        const layer = topLayerTransformerRef.current.getLayer();
        if (layer) {
          layer.batchDraw();
        }
      }
      lastAttachedNodeRef.current = null;
      return;
    }

    const runAttach = () => {
      // First, clear any existing transformers to prevent conflicts when switching between object types
      clearTransformers(
        transformerRef,
        offCanvasTransformerRef,
        topLayerTransformerRef
      );

      const attachResult = attachTransformerToNode({
        selectedObject,
        stageRef,
        offCanvasStageRef,
        topLayerStageRef,
        transformerRef,
        offCanvasTransformerRef,
        topLayerTransformerRef,
        rectangles,
        circles,
        images,
        texts,
        arrows,
        lines,
        doubleSidedArrows,
        offCanvasRectangles,
        offCanvasCircles,
        offCanvasImages,
        offCanvasTexts,
        offCanvasArrows,
        offCanvasLines,
        offCanvasDoubleSidedArrows,
      });

      const { node, sourceStage, targetTransformer } = attachResult;

      if (node && sourceStage && targetTransformer) {
        // Validate node still exists
        if (
          !validateNode(
            node,
            transformerRef,
            offCanvasTransformerRef,
            topLayerTransformerRef
          )
        ) {
          lastAttachedNodeRef.current = null;
          return;
        }

        // For images, ensure the node dimensions match the state
        applyImageNodeProperties(node, selectedObject);

        // CRITICAL FIX: For images, set position to match selectedObject props
        // This ensures correct positioning, especially for useTopLeftAnchor images
        // after applyImageNodeProperties may have changed offsets
        if (selectedObject.type === "Image") {
          node.x(selectedObject.x);
          node.y(selectedObject.y);
        }

        targetTransformer.nodes([node]);
        if (
          selectedObject.type === "Text" &&
          typeof targetTransformer.forceUpdate === "function"
        ) {
          targetTransformer.forceUpdate();
        }
        if (isDuplicatePlacementActive) {
          applyDuplicatePlacementTransformerAttrs(targetTransformer);
        }

        targetTransformer.getLayer().batchDraw();

        // PERFORMANCE FIX: Mark this node as attached with its settings
        lastAttachedNodeRef.current = cacheKey;
      } else {
        // PERFORMANCE FIX: Only retry once with a single short delay
        const retryTimeout = setTimeout(() => {
          const retryResult = attachTransformerToNode({
            selectedObject,
            stageRef,
            offCanvasStageRef,
            topLayerStageRef,
            transformerRef,
            offCanvasTransformerRef,
            topLayerTransformerRef,
            rectangles,
            circles,
            images,
            texts,
            arrows,
            lines,
            doubleSidedArrows,
            offCanvasRectangles,
            offCanvasCircles,
            offCanvasImages,
            offCanvasTexts,
            offCanvasArrows,
            offCanvasLines,
            offCanvasDoubleSidedArrows,
          });

          if (
            retryResult.node &&
            retryResult.sourceStage &&
            retryResult.targetTransformer
          ) {
            applyImageNodeProperties(retryResult.node, selectedObject);
            if (selectedObject.type === "Image") {
              retryResult.node.x(selectedObject.x);
              retryResult.node.y(selectedObject.y);
            }
            retryResult.targetTransformer.nodes([retryResult.node]);
            if (
              selectedObject.type === "Text" &&
              typeof retryResult.targetTransformer.forceUpdate === "function"
            ) {
              retryResult.targetTransformer.forceUpdate();
            }
            if (isDuplicatePlacementActive) {
              applyDuplicatePlacementTransformerAttrs(
                retryResult.targetTransformer,
              );
            }
            retryResult.targetTransformer.getLayer().batchDraw();
            lastAttachedNodeRef.current = cacheKey;
          } else {
            clearTransformers(
              transformerRef,
              offCanvasTransformerRef,
              topLayerTransformerRef
            );
            lastAttachedNodeRef.current = null;
          }
        }, 50);

        return () => clearTimeout(retryTimeout);
      }
    };

    // Duplicate/cloning: attach synchronously so default anchors never paint for a frame (RAF runs after paint).
    if (isDuplicatePlacementActive) {
      runAttach();
      return undefined;
    }

    const rafId = requestAnimationFrame(runAttach);
    return () => cancelAnimationFrame(rafId);
  }, [
    selectedObject?.id,
    selectedObject?.type, // Only re-run if ID or type changes
    selectedObject?.maintainAspectRatio, // CRITICAL FIX: Re-run when aspect ratio setting changes
    selectedObject?._canvas, // Re-run when element moves between main canvas and off-canvas
    selectedObject?.width, // Re-run when image geometry changes (e.g., replace image)
    selectedObject?.height, // Re-run when image geometry changes (e.g., replace image)
    selectedObject?.scaleX, // Re-run when image scaling changes
    selectedObject?.scaleY, // Re-run when image scaling changes
    selectedObject?.src, // Re-run when image source is replaced
    selectedObject?._replacedAt, // Re-run immediately after replace/crop marker is set
    selectedObjects?.length, // Only re-run if selection count changes
    stageRef,
    offCanvasStageRef,
    topLayerStageRef,
    transformerRef,
    offCanvasTransformerRef,
    topLayerTransformerRef,
    isTransformingRef,
    isDuplicatePlacementActive,
  ]);

  // Effect to clear transformers when selectedObject is null (e.g., after deletion)
  useEffect(() => {
    if (!selectedObject) {
      clearTransformers(
        transformerRef,
        offCanvasTransformerRef,
        topLayerTransformerRef
      );
      lastAttachedNodeRef.current = null;
    }
  }, [
    selectedObject,
    transformerRef,
    offCanvasTransformerRef,
    topLayerTransformerRef,
  ]);
};
